import {captureException} from '@sentry/browser';
import {Button, DataStatus} from 'platform/components';
import {Center, Show, VStack} from 'platform/foundation';

import {usePostOAuthTokenMutation} from '@dms/api/base';
import {appWorkspaceKey, browserStorageKey} from '@dms/config';
import i18n from '@dms/i18n';
import {
  buildUrl,
  dashboardRoutes,
  iamRedirectRoute,
  redirectToLogin,
  workspaceInitializeRoute,
  workspaceRedirectRoute,
} from '@dms/routes';
import {CommonSkeleton, handleApiError} from '@dms/shared';

import {useNavigate, useOnMount} from 'shared/web';

import {getRedirectSearchParams} from './utils/getRedirectSearchParams';

export const WorkspaceRedirect = () => {
  const [postOAuthToken, {isError}] = usePostOAuthTokenMutation();
  const navigate = useNavigate();

  const [areSearchParamsValid, params] = getRedirectSearchParams();

  useOnMount(() => {
    if (!areSearchParamsValid) {
      captureException(new Error('FATAL: Invalid redirect search params'), {extra: {params}});
      return;
    }

    const redirect_uri = buildUrl(workspaceInitializeRoute, appWorkspaceKey, false);

    postOAuthToken({
      grant_type: 'authorization_code',
      code: params.code,
      redirect_uri,
      workspace: params.workspace,
      client_id: 'dms',
    })
      .unwrap()
      .then((response) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, response.access_token);
        if (response.refresh_token) {
          localStorage.setItem(browserStorageKey.REFRESH_TOKEN, response.refresh_token);
        }

        navigate(dashboardRoutes.home);
      })
      .catch(handleApiError);
  });

  return (
    <Center height="100vh" width="100%">
      <VStack spacing={4}>
        <DataStatus
          isLoading
          isError={isError || !areSearchParamsValid}
          errorMessage={
            areSearchParamsValid
              ? i18n.t('general.errors.invalidLoginAttempt')
              : i18n.t('general.errors.invalidParams')
          }
        />

        <Show when={isError || !areSearchParamsValid}>
          <Button
            title={i18n.t('page.login.actions.backToLogin')}
            onClick={() =>
              redirectToLogin({redirectUrl: buildUrl(iamRedirectRoute, params?.workspace, false)})
            }
          />
        </Show>
      </VStack>
    </Center>
  );
};

export const WorkspaceInitialize = () => {
  const [areSearchParamsValid, params] = getRedirectSearchParams();

  useOnMount(() => {
    if (areSearchParamsValid) {
      window.location.href = buildUrl(workspaceRedirectRoute, params.workspace);
    }
  });

  return <CommonSkeleton />;
};
